// Change tracking for the backup reminder (SPEC F8: "more than 14 days old and there are unsaved changes").
// A Dexie hook on every table that holds your own data notes that something changed; a short debounce then
// writes `lastChangeAt` (and `firstChangeAt`, once) to the settings row. Settings and continuity are not
// tracked: they change on every scroll and carry nothing you'd miss. The crate cache is not yours.
import { DEFAULT_SETTINGS } from './repos/settings';
import { getDb, type LowEndDb } from './schema';

const TRACKED = ['samples', 'blobs', 'kits', 'patterns', 'marks', 'history', 'channels', 'projects', 'records', 'sessions', 'cues', 'eqTraining', 'compTraining'] as const;
const DEBOUNCE_MS = 1500;

let paused = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let pending = false;
let clock: () => number = Date.now;
const installed = new WeakSet<LowEndDb>();
let target: LowEndDb | undefined;

async function write(): Promise<void> {
  if (!pending) return;
  pending = false;
  const db = target ?? getDb();
  const at = clock();
  await db.transaction('rw', db.settings, async () => {
    const cur = (await db.settings.get('app')) ?? DEFAULT_SETTINGS;
    await db.settings.put({ ...cur, key: 'app', lastChangeAt: at, firstChangeAt: cur.firstChangeAt ?? at });
  });
}

function note(): void {
  if (paused > 0) return;
  pending = true;
  clearTimeout(timer);
  timer = setTimeout(() => { void write().catch(() => undefined); }, DEBOUNCE_MS);
}

/** Starts watching. Safe to call twice; call it before the first read so the hooks are in place when the database opens. */
export function trackChanges(db: LowEndDb = getDb(), now: () => number = Date.now): void {
  if (installed.has(db)) return;
  installed.add(db);
  target = db;
  clock = now;
  for (const name of TRACKED) {
    const table = db.table(name);
    table.hook('creating', () => { note(); });
    table.hook('updating', () => { note(); });
    table.hook('deleting', () => { note(); });
  }
}

/** Runs `fn` without noting changes: a restore or a reset puts the data in a known state, not a new one. */
export async function withoutTracking<T>(fn: () => Promise<T>): Promise<T> {
  paused++;
  try {
    return await fn();
  } finally {
    paused--;
    // Whatever was noted before the pause still counts; nothing noted during it does.
    if (paused === 0 && !pending) clearTimeout(timer);
  }
}

/** Writes a noted change now instead of after the debounce. Used when leaving the page, and by tests. */
export function flushChanges(): Promise<void> {
  clearTimeout(timer);
  return write();
}
